import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye, Copy, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { memo, useState } from 'react'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import type { Contact } from '@/types/entities'

interface EmailCellProps {
    email: string | null | undefined
}

const EmailCell = memo(function EmailCell({ email }: EmailCellProps) {
    const { toast } = useToast()
    const { t } = useTranslation()
    const [copied, setCopied] = useState(false)
    
    const handleCopy = async () => {
        if (!email) return
        try {
            await navigator.clipboard.writeText(email)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
            toast({
                title: t('contacts.copied'),
                description: t('contacts.emailCopied'),
            })
        } catch (error) {
            toast({
                title: t('contacts.copyFailed'),
                description: t('contacts.failedToCopy'),
                variant: 'destructive',
            })
        }
    }
    
    if (!email) {
        return <span className="text-muted-foreground">—</span>
    }
    
    return (
        <div className="flex items-center gap-2">
            <span>{email}</span>
            <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                    e.stopPropagation()
                    handleCopy()
                }}
                title={t('contacts.copyEmail')}
            >
                {copied ? (
                    <Check className="h-3 w-3 text-green-600" />
                ) : (
                    <Copy className="h-3 w-3" />
                )}
            </Button>
        </div>
    )
})

interface PhoneCellProps {
    phonePrefix: string | null | undefined
    phone: string | null | undefined
}

const PhoneCell = memo(function PhoneCell({ phonePrefix, phone }: PhoneCellProps) {
    const { toast } = useToast()
    const { t } = useTranslation()
    const [copied, setCopied] = useState(false)
    
    const handleCopy = async () => {
        const phoneNumber = `${phonePrefix || ''}${phone || ''}`.trim()
        if (!phoneNumber) return
        try {
            await navigator.clipboard.writeText(phoneNumber)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
            toast({
                title: t('contacts.copied'),
                description: t('contacts.phoneCopied'),
            })
        } catch (error) {
            toast({
                title: t('contacts.copyFailed'),
                description: t('contacts.failedToCopy'),
                variant: 'destructive',
            })
        }
    }
    
    if (!phonePrefix && !phone) {
        return <span className="text-muted-foreground">—</span>
    }
    
    return (
        <div className="flex items-center gap-2">
            <span>
                {phonePrefix && <span>{phonePrefix} </span>}
                {phone || <span className="text-muted-foreground">—</span>}
            </span>
            <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                    e.stopPropagation()
                    handleCopy()
                }}
                title={t('contacts.copyPhone')}
            >
                {copied ? (
                    <Check className="h-3 w-3 text-green-600" />
                ) : (
                    <Copy className="h-3 w-3" />
                )}
            </Button>
        </div>
    )
})

interface ContactActionsProps {
    contact: Contact
    onEdit: (contact: Contact) => void
    onDelete: (contact: Contact) => void
    onView: (contact: Contact) => void
}

const ContactActions = memo(function ContactActions({
    contact,
    onEdit,
    onDelete,
    onView,
}: ContactActionsProps) {
    const [open, setOpen] = useState(false)

    return (
        <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onView(contact)
                        setOpen(false)
                    }}
                >
                    <Eye className="mr-2 h-4 w-4" />
                    View
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onEdit(contact)
                        setOpen(false)
                    }}
                >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onDelete(contact)
                        setOpen(false)
                    }}
                    className="text-destructive"
                >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
})

export function createContactColumns(
    onEdit: (contact: Contact) => void,
    onDelete: (contact: Contact) => void,
    onView: (contact: Contact) => void,
    t: (key: string) => string
): ColumnDef<Contact>[] {
    return [
        {
            accessorKey: 'firstName',
            header: t('contacts.firstName'),
            cell: ({ row }) => {
                const contact = row.original
                return (
                    <div className="font-medium">{contact.firstName || <span className="text-muted-foreground">—</span>}</div>
                )
            },
        },
        {
            accessorKey: 'surname',
            header: t('contacts.surname'),
            cell: ({ row }) => {
                const contact = row.original
                return contact.surname || <span className="text-muted-foreground">—</span>
            },
        },
        {
            accessorKey: 'email',
            header: t('contacts.email'),
            cell: ({ row }) => {
                const contact = row.original
                return <EmailCell email={contact.email} />
            },
        },
        {
            accessorKey: 'phone',
            header: t('contacts.phone'),
            cell: ({ row }) => {
                const contact = row.original
                return <PhoneCell phonePrefix={contact.phonePrefix} phone={contact.phone} />
            },
        },
        {
            accessorKey: 'position',
            header: t('contacts.position'),
            cell: ({ row }) => {
                const contact = row.original
                return contact.position || <span className="text-muted-foreground">—</span>
            },
        },
        {
            accessorKey: 'team',
            header: t('contacts.team'),
            cell: ({ row }) => {
                const contact = row.original
                return contact.team || <span className="text-muted-foreground">—</span>
            },
        },
        {
            id: 'actions',
            header: t('common.actions'),
            cell: ({ row }) => {
                const contact = row.original
                return (
                    <ContactActions
                        contact={contact}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onView={onView}
                    />
                )
            },
        },
    ]
}
